<template>
	<div
		class="overlay-product"
		v-if="product"
	>
		<div class="overlay-product__details">
			<div class="overlay-product__images">
				<button
					class="overlay-product__message"
					ref="message"
					:class="{'overlay-product__message--added': addedToCart}"
					@click="onCloseMessage"
					v-html="$t('shoplist.product.successCart')"
				/>
				<ul
					class="overlay-product__images-grid"
					:class="[
						`overlay-product__images-grid--${Math.min(product.imagesDetail.length, 3)}`
					]"
					v-if="showImageGrid"
				>
					<li
						v-for="(image, index) in product.imagesDetail.slice(0, 3)"
						:key="`product-image-${index}`"
						class="overlay-product__images-item"
					>
						<img
							class="overlay-product__images-item-image"
							:src="image"
							alt="Product Image"
							data-object-fit
							loading="lazy"
						>
					</li>
				</ul>
				<vue-glide
					ref="glide"
					v-bind="glideOptions"
					class="overlay-product__images-slider"
					:class="{
						'overlay-product__images-slider--disabled': this.product.imagesDetail.length <= 1
					}"
					v-else
				>
					<vue-glide-slide
						class="overlay-product__images-slide"
						v-for="(image, index) in product.imagesDetail"
						:key="`product-image-slide-${index}`"
					>
						<img
							ref="image"
							class="overlay-product__images-slide-image"
							:src="image"
							alt="Product Image"
							data-object-fit
							loading="lazy"
						>
					</vue-glide-slide>
				</vue-glide>
			</div>
			<div
				class="overlay-product__info"
				:class="[
					bgColorClass
				]"
			>
				<div class="overlay-product__controls">
					<a
						class="overlay-product__back-button"
						@click.prevent="$emit('close')"
						v-html="$t('shoplist.product.continueShopping')"
					/>
					<button
						class="overlay-product__close"
						@click.prevent="$emit('close')"
					>
						<SVGClose/>
					</button>
				</div>

				<div
					class="overlay-product__info-content"
					:class="[
						bgColorClass
					]"
				>
					<h1
						class="overlay-product__title"
					>
						<span
							class="overlay-product__title-name"
							v-html="product.name"
						/>
						<SVGLabelLeaf
							class="products__item-title-label"
							v-if="product.isVegetarian || product.isVegan"
						/>
						<SVGLabelLeaf
							class="products__item-title-label"
							v-if="product.isVegan"
						/>
					</h1>

					<div
						class="overlay-product__description"
						v-if="product.description || product.shortDescription"
						v-html="product.description || product.shortDescription"
					/>

					<ul
						class="overlay-product__preparation"
						v-if="product.consumption || product.steamtime"
					>
						<li
							class="overlay-product__preparation-item"
							v-if="product.consumption"
						>
							<SVGDumplingSticks class="overlay-product__preparation-item-icon"/>
							<div class="overlay-product__preparation-item-content">
								<span
									class="overlay-product__preparation-item-text"
									v-html="product.consumption"
								/>
							</div>
						</li>
						<li
							class="overlay-product__preparation-item"
							v-if="product.steamtime"
						>
							<SVGDumplingsBasket class="overlay-product__preparation-item-icon"/>
							<div class="overlay-product__preparation-item-content">
								<span
									class="overlay-product__preparation-item-text"
									v-html="product.steamtime"
								/>
								<Button
									v-if="$te('shoplist.product.steamTimeLinkText') && $te('shoplist.product.steamTimeLinkUrl')"
									class="overlay-product__preparation-item-button"
									level="tertiary"
									:text="$t('shoplist.product.steamTimeLinkText')"
									:href="$t('shoplist.product.steamTimeLinkUrl')"
									target="_blank"
									arrow
								/>
							</div>
						</li>
					</ul>

					<Collapsible
						v-if="product.nutritionInfos"
						class="overlay-product__nutrition"
						:initial-open="false"
					>
						<template slot="trigger">
							<h2
								class="overlay-product__nutrition-title"
								v-html="$t('shoplist.product.nutritional')"
							/>
						</template>
						<div
							class="overlay-product__nutrition-infos"
							v-html="product.nutritionInfos"
						/>
					</Collapsible>

					<div
						v-if="product.isNewFlavour"
						class="overlay-product__new"
						v-html="$t('shoplist.product.newFlavour')"
					/>
					<div
						v-else-if="product.isPlanted"
						class="overlay-product__planted"
					>
						<img
							class="overlay-product__planted-label"
							:src="productLabelPath('planted')"
							alt="planted label"
						>
					</div>
					<div
						v-else-if="product.isLimited"
						class="overlay-product__limited"
						v-html="$t('shoplist.product.limitedEdition')"
					/>

					<div
						class="overlay-product__price"
					>
						<strike
							class="overlay-product__price-old"
							v-if="originalPrice"
							v-html="$options.filters.madpricecent(originalPrice)"
						/>
						<span
							class="overlay-product__price-current"
							v-html="$options.filters.madpricecent(price)"
						/>
						<span
							class="overlay-product__price-unit"
							v-if="product.unit"
							v-html="product.unit"
						/>
						<!-- <span
							class="overlay-product__price-available"
							v-if="product.unit"
							v-html="product.unit"
						/> -->
					</div>
				</div>

				<div
					class="overlay-product__cart"
					v-if="hasBasket"
				>
					<QuantityCounter
						:quantity="quantity"
						base-class="overlay-product__quantity"
						:class="{
							'overlay-product__quantity--soldout': !isAvailable,
						}"
						@sub="onSub()"
						@add="onAdd()"
						v-if="!product.isEditable"
						:locked="!isAvailable"
					/>
					<Button
						class="overlay-product__cart-button"
						:class="{
							'overlay-product__cart-button--soldout': !isAvailable,
							'btn--no-animation': !isAvailable || locked,
						}"
						@click="onCart()"
						level="primary"
						type="button"
						:text="isAvailable ? $t('shoplist.product.add') : $t('shoplist.product.soldOut')"
						:disabled="locked || !isAvailable"
						:variants="[
							'small'
						]"
					/>
				</div>

			</div>
		</div>

		<ProductCrossSell
			v-if="crossSell"
			:products="crossSell"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import MixinProductLabels from "@/mixins/MixinProductLabels";
import QuantityCounter from '@/components/QuantityCounter';
import ProductCrossSell from '@/components/ProductCrossSell';
import Collapsible from '@/components/Collapsible';
import Button from '@/components/Button';
import { Glide, GlideSlide } from 'vue-glide-js'
import breakpointMatch from '@/utils/breakpointMatch';

export default {
	components: {
		'vue-glide': Glide,
		'vue-glide-slide': GlideSlide,
		QuantityCounter,
		Button,
		ProductCrossSell,
		Collapsible,
	},
	mixins: [
		MixinProductLabels,
	],
	props: {
		product: Object,
		locked: Boolean,
	},
	data() {
		return {
			quantity: 1,
			selectedVariant: this.product.variants.findIndex(v => v.code == this.product.selectedVariant),
			addedToCart: false,
			showImageGrid: true,
			glideOptions: {
				bullet: this.product.imagesDetail.length > 1,
				type: 'carousel',
				startAt: 0,
				perView: 1,
				focusAt: 'center',
				gap: 0,
				autoplay: 0,
				hoverpause: true,
				keyboard: false,
				bound: false,
				swipeThreshold: 80,
				dragThreshold: 120,
				perTouch: 0,
				touchRatio: 0.5,
				touchAngle: 45,
				animationDuration: 100,
				rewind: true,
				peek: 0,
			},
		}
	},
	computed: {
		...mapGetters('cart', [
			'hasBasket',
		]),
		bgColorClass() {
			return `madamesum--bg-${this.product.bgColor || 'green'}`;
		},
		price() {
			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				return this.product.variants[this.selectedVariant].price;
			}
			return this.product.price;
		},
		originalPrice() {
			let originalPrice = this.product.originalPrice;

			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				originalPrice = this.product.variants[this.selectedVariant].originalPrice;
			}
			return !!originalPrice && this.price < originalPrice && originalPrice;
		},
		priceReduction() {
			if (this.originalPrice) {
				return `-${Math.floor(100 / this.originalPrice * (this.originalPrice - this.price))}%`;
			}
			return false;
		},
		variant() {
			if (this.product.variants.length) {
				if (this.product.variants.length > this.selectedVariant) {
					return this.product.variants[this.selectedVariant];
				}
				return this.product.variants[0];
			}
			else {
				return false;
			}
		},
		availableVariants() {
			// only show variants that have stock
			return this.product.variants.filter(v => v.stock != 0)
		},
		stock() {
			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				if (!isNaN(this.product.variants[this.selectedVariant].stock)) {
					return this.product.variants[this.selectedVariant].stock;
				}
			}
			return this.product.stock;
		},
		isAvailable() {
			return this.stock > 0;
		},
		crossSell() {
			return this.product.associations.product_crosssell
		},
	},
	watch: {
		locked: function (value) {
			if (value) {
				this.onAddedToCart();
			}
		}
	},
	methods: {
		onSub() {
			this.quantity = Math.max(this.quantity - 1, 1);
			this.$emit('quantitychange', this.quantity);
		},
		onAdd() {
			this.quantity = this.quantity + 1;
			this.$emit('quantitychange', this.quantity);
		},
		onConfig() {
			const item = {
				...this.product,
				price: this.price,
				variant: this.variant.name,
				selectedVariant: this.variant.code,
			}

			this.$emit('config', item);
		},
		onCart() {
			this.addedToCart = false;

			const item = {
				quantity: this.quantity,
				productCode: this.product.code,
				variantCode: this.variant.code
			}
			if (this.product.isEditable) {
				item.forceCopy = true
			}

			this.$emit('addToCart', item);

			this.$nextTick(() => {
				this.quantity = 1;
			});
		},
		onAddedToCart() {
			this.addedToCart = true;

			// if (this.$refs.message.getBoundingClientRect().top < 0) {
			// 	scrrroll.to(this.$refs.message, 300);
			// }

			clearTimeout(this.messageTimeout);
			this.messageTimeout = setTimeout(() => {
				this.addedToCart = false;
			}, 6000);
		},
		onCloseMessage(e) {
			clearTimeout(this.messageTimeout);
			this.addedToCart = false;
		},
		onResize() {
			this.showImageGrid = breakpointMatch('sm');
		}
	},
	mounted() {
		this.onResize();
		window.addEventListener('resize', this.onResize);
	},
	destroyed() {
		window.removeEventListener('resize', this.onResize);
	}
}
</script>

<style>

</style>
